<template>
  <div class="home-container">
    <!-- 顶部横幅 -->
    <div class="banner" :style="homeBackground ? { backgroundImage: `url(${homeBackground})` } : {}">
      <div class="banner-content">
        <div class="logo-area">
          <img v-if="systemLogo" :src="systemLogo" class="logo-img" alt="Logo" />
          <span v-else class="logo">LOGO</span>
          <span v-if="systemName" class="system-name">{{ systemName }}</span>
        </div>
        <div v-if="isLoggedIn" class="user-area" ref="userArea">
          <div class="user-info" @click="showUserMenu = !showUserMenu">
            <img :src="userAvatar" class="avatar" />
            <span class="user-email">{{ userInfo.email || userInfo.userName }}</span>
          </div>
          <div v-if="showUserMenu" class="user-menu">
            <div class="menu-header">
              <img :src="userAvatar" class="menu-avatar" />
              <span class="menu-nickname">{{ userInfo.email || userInfo.userName }}</span>
            </div>
            <div class="menu-divider"></div>
            <div class="menu-actions">
              <div class="menu-item" @click="goProfile">
                <img src="@/assets/image/geren.png" class="menu-icon" />
                <span>个人信息</span>
              </div>
              <div class="menu-item" @click="goFavorite">
                <img src="@/assets/image/shoucang.png" class="menu-icon" />
                <span>收藏</span>
              </div>
              <div class="menu-item" @click="goExamRecord">
                <img src="@/assets/image/kaoshi.png" class="menu-icon" />
                <span>考试</span>
              </div>
            </div>
            <div class="menu-logout" @click="handleLogout">
              <span>退出</span>
              <img src="@/assets/image/quit.png" class="logout-icon" />
            </div>
          </div>
        </div>
        <button v-else class="login-btn" @click="goLogin">登录</button>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content">
      <!-- 一级分类：课程 -->
      <div class="category-tabs">
        <span 
          v-for="(cat, index) in categories" 
          :key="index"
          :class="['tab', { active: activeCategory === index }]"
          @click="selectCategory(index)"
        >{{ cat.name }}</span>
      </div>

      <!-- 二级分类：科目 -->
      <div class="sub-tabs">
        <span 
          v-for="(sub, index) in subCategories" 
          :key="index"
          :class="['sub-tab', { active: activeSubCategory === index }]"
          @click="selectSubCategory(index)"
        >{{ sub.name }}</span>
      </div>

      <div class="content-wrapper">
        <!-- 左侧内容 -->
        <div class="left-content">
          <!-- 模式切换 -->
          <div class="mode-tabs">
            <div 
              v-for="(mode, index) in modes" 
              :key="index"
              :class="['mode-tab', { active: activeMode === index }]"
              @click="selectMode(index)"
            >{{ mode }}</div>
          </div>

          <!-- 进度条 -->
          <div v-if="activeMode !== 2" class="progress-bar-wrapper">
            <div class="progress-info">
              <span>学习进度</span>
              <span :class="['progress-text', progressPercent >= 50 ? 'green' : 'red']">{{ progressPercent }}%</span>
            </div>
            <div class="progress-bar">
              <div class="progress-fill" :style="{ width: progressPercent + '%', background: progressPercent >= 50 ? 'linear-gradient(90deg, #3d8c40, #5cb85c)' : 'linear-gradient(90deg, #e74c3c, #ff6b6b)' }"></div>
            </div>
          </div>

          <!-- 节列表 - 练习/真题模式 -->
          <div v-if="activeMode !== 2" class="chapters-wrapper">
            <div class="chapter-section" v-for="(chapter, cIndex) in chapters" :key="cIndex">
              <h3 class="chapter-title">{{ chapter.title }}</h3>
              <div class="topic-grid">
                <div 
                  class="topic-card" 
                  v-for="(topic, tIndex) in chapter.topics" 
                  :key="tIndex"
                  @click="goToTopic(topic)"
                >
                  <div class="topic-name">{{ topic.name }}</div>
                  <div class="topic-progress">已做<span class="done">{{ topic.done }}</span>/{{ topic.total }}题</div>
                </div>
              </div>
            </div>
          </div>

          <!-- 考试模式 -->
          <div v-else class="exam-wrapper">
            <div v-if="!examInfo" class="no-exam">
              <p>暂无考试安排</p>
            </div>
            <div v-else class="exam-content">
              <!-- 顶部：倒计时和状态 -->
              <div class="exam-header">
                <div class="countdown-box">
                  <div class="exam-title">{{ examInfo.examTitle }}</div>
                  <div class="countdown-title">{{ examInfo.status === '1' ? '考试进行中' : '距离考试开始' }}</div>
                  <div v-if="examInfo.status !== '1'" class="countdown-numbers">
                    <div class="countdown-item">
                      <span class="num">{{ countdown.days }}</span>
                      <span class="label">天</span>
                    </div>
                    <div class="countdown-item">
                      <span class="num">{{ countdown.hours }}</span>
                      <span class="label">时</span>
                    </div>
                    <div class="countdown-item">
                      <span class="num">{{ countdown.minutes }}</span>
                      <span class="label">分</span>
                    </div>
                    <div class="countdown-item">
                      <span class="num">{{ countdown.seconds }}</span>
                      <span class="label">秒</span>
                    </div>
                  </div>
                  <div class="exam-time-info">
                    <span>开始：{{ formatExamTime(examInfo.startTime) }}</span>
                    <span class="divider">|</span>
                    <span>时长：{{ examInfo.duration }}分钟</span>
                    <span class="divider">|</span>
                    <span>总分：{{ examInfo.totalScore }}分</span>
                  </div>
                </div>
                <button 
                  class="start-exam-btn" 
                  :class="{ disabled: examInfo.status !== '1' }"
                  :disabled="examInfo.status !== '1'"
                  @click="startExam"
                >
                  {{ examInfo.status === '1' ? '进入考试' : (examInfo.status === '0' ? '未开始' : '已结束') }}
                </button>
              </div>
              <!-- 底部：考试须知 -->
              <div class="exam-notice">
                <h3>考试须知</h3>
                <div class="notice-content" v-if="examInfo.notice" v-html="examInfo.notice.replace(/\n/g, '<br/>')"></div>
                <div class="notice-content" v-else>
                  <p>1、本次模拟考试时长为{{ examInfo.duration }}分钟，系统将在考试开始后倒计时，时间结束自动提交试卷。</p>
                  <p>2、请在安静、独立的环境中作答，确保网络稳定。</p>
                  <p>3、考试过程中请勿刷新页面或退出，以免影响答题进度。</p>
                </div>
              </div>
            </div>
          </div>

          <div class="content-decor"></div>
        </div>

        <!-- 右侧日历和活动 -->
        <div class="right-sidebar">
          <div class="sidebar-card">
            <div class="week-header">
              <div 
                v-for="(day, index) in weekDays" 
                :key="index"
                :class="['week-day', { active: activeDay === index, 'has-activity': day.hasActivity }]"
                @click="selectDay(index)"
              >
                <span class="day-label">{{ day.label }}</span>
                <span class="day-date">{{ day.date }}</span>
              </div>
            </div>
            <div class="activity-section">
              <div v-if="dayActivities.length > 0">
                <div 
                  class="activity-item" 
                  v-for="activity in dayActivities" 
                  :key="activity.activityId"
                  @click="goActivityDetail(activity.activityId)"
                >
                  <span class="activity-type">{{ activity.typeName || '活动' }}</span>
                  <span class="activity-title">{{ activity.title }}</span>
                  <a href="javascript:;" class="view-more">查看 ></a>
                </div>
              </div>
              <div v-else class="no-activity">当日暂无活动</div>
            </div>
            <div class="sidebar-decor"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部 -->
    <div class="footer">
      <p v-if="systemConfig.policeFiling">{{ systemConfig.policeFiling }}</p>
      <p v-if="systemConfig.icpFiling">{{ systemConfig.icpFiling }}</p>
    </div>
  </div>
</template>

<script>
import { getWeekActivities } from '@/api/activity'
import { getSubjects, getCourses, getChapters, getSections, getModeProgress } from '@/api/course'
import { getLatestExam, startExam } from '@/api/exam'
import { getSystemConfig } from '@/api/systemConfig'
import { encodeId } from '@/utils/idEncoder'
import config from '@/utils/config'

export default {
  name: 'HomePage',
  data() {
    return {
      activeCategory: 0,      // 第一行：课程
      activeSubCategory: 0,   // 第二行：科目
      activeChapter: 0,       // 第三行：章
      activeMode: 0,
      activeDay: 0,
      showUserMenu: false,
      categories: [],         // 课程列表 (dt_subject)
      subCategories: [],      // 科目列表 (dt_course)
      chapterTabs: [],        // 章列表 (dt_chapter)
      modes: ['练习模式', '真题模式', '考试模式'],
      modeProgress: { total: 0, done: 0 },  // 模式进度
      weekDays: [],
      weekActivities: [],
      chapters: [],           // 节列表 (dt_section) - 显示为卡片
      userInfo: null,
      countdown: {
        days: '00',
        hours: '00',
        minutes: '00',
        seconds: '00'
      },
      examInfo: null,
      countdownTimer: null,
      homeBackground: '',
      systemLogo: '',
      systemName: '',
      systemConfig: {
        icpFiling: '',
        policeFiling: ''
      },
      examRequestId: 0  // 用于防止旧请求覆盖新请求
    }
  },
  computed: {
    isLoggedIn() {
      return this.userInfo !== null
    },
    userAvatar() {
      if (this.userInfo && this.userInfo.avatar) {
        // 如果头像是相对路径，拼接后端地址
        if (this.userInfo.avatar.startsWith('http')) {
          return this.userInfo.avatar
        }
        return config.imageBaseURL + this.userInfo.avatar
      }
      // 默认头像使用系统logo
      return require('@/assets/image/touxiang.png')
    },
    dayActivities() {
      if (!this.weekDays[this.activeDay]) return []
      const selectedDate = this.weekDays[this.activeDay].fullDate
      return this.weekActivities.filter(a => {
        const actDate = new Date(a.activityTime)
        const actDateStr = `${actDate.getFullYear()}-${String(actDate.getMonth() + 1).padStart(2, '0')}-${String(actDate.getDate()).padStart(2, '0')}`
        return actDateStr === selectedDate
      })
    },
    progressPercent() {
      if (this.modeProgress.total === 0) return 0
      return Math.round((this.modeProgress.done / this.modeProgress.total) * 100)
    }
  },
  created() {
    // 检查登录状态
    const stored = localStorage.getItem('userInfo')
    if (stored) {
      this.userInfo = JSON.parse(stored)
    }
    // 加载系统配置
    this.loadSystemConfig()
    // 初始化本周日期
    this.initWeekDays()
    // 加载本周活动
    this.loadWeekActivities()
    // 加载科目列表
    this.loadSubjects()
    // 点击页面任意位置关闭菜单
    document.addEventListener('click', this.handleClickOutside)
  },
  methods: {
    async loadSystemConfig() {
      try {
        const res = await getSystemConfig()
        if (res.data) {
          if (res.data.homeBackground) {
            this.homeBackground = res.data.homeBackground
          }
          if (res.data.systemLogo) {
            this.systemLogo = res.data.systemLogo
          }
          if (res.data.systemName) {
            this.systemName = res.data.systemName
          }
          this.systemConfig = {
            icpFiling: res.data.icpFiling || '',
            policeFiling: res.data.policeFiling || ''
          }
        }
      } catch (error) {
        console.error('获取系统配置失败', error)
      }
    },
    handleClickOutside(e) {
      // 如果点击的不是用户区域，关闭菜单
      if (this.showUserMenu && this.$refs.userArea && !this.$refs.userArea.contains(e.target)) {
        this.showUserMenu = false
      }
    },
    initWeekDays() {
      const today = new Date()
      const dayOfWeek = today.getDay() // 0是周日，1-6是周一到周六
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
      const monday = new Date(today)
      monday.setDate(today.getDate() + mondayOffset)
      
      const labels = ['周一', '周二', '周三', '周四', '周五', '周六', '周日']
      const days = []
      
      for (let i = 0; i < 7; i++) {
        const d = new Date(monday)
        d.setDate(monday.getDate() + i)
        const fullDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        days.push({
          label: labels[i],
          date: d.getDate(),
          fullDate: fullDate,
          hasActivity: false
        })
      }
      
      this.weekDays = days
      // 设置当天为选中状态
      const todayIndex = dayOfWeek === 0 ? 6 : dayOfWeek - 1
      this.activeDay = todayIndex
    },
    async loadWeekActivities() {
      try {
        const res = await getWeekActivities()
        this.weekActivities = res.data || []
        // 标记有活动的日期
        this.weekActivities.forEach(a => {
          const actDate = new Date(a.activityTime)
          const actDateStr = `${actDate.getFullYear()}-${String(actDate.getMonth() + 1).padStart(2, '0')}-${String(actDate.getDate()).padStart(2, '0')}`
          const dayIndex = this.weekDays.findIndex(d => d.fullDate === actDateStr)
          if (dayIndex >= 0) {
            this.weekDays[dayIndex].hasActivity = true
          }
        })
      } catch (error) {
        console.error('加载活动失败', error)
      }
    },
    // 加载课程列表（第一行tab）- dt_subject表
    async loadSubjects() {
      try {
        const gradeId = this.userInfo ? this.userInfo.gradeId : null
        const res = await getSubjects(gradeId)
        const subjects = res.data || []
        // dt_subject表返回 subjectId, subjectName
        this.categories = subjects.map(s => ({ id: s.subjectId, name: s.subjectName }))
        if (this.categories.length > 0) {
          this.loadCourses()
        } else {
          this.subCategories = []
          this.chapterTabs = []
          this.chapters = []
        }
      } catch (error) {
        console.error('加载课程失败', error)
      }
    },
    // 加载科目列表（第二行tab）- dt_course表
    async loadCourses() {
      if (this.categories.length === 0) return
      const subjectId = this.categories[this.activeCategory].id
      try {
        const res = await getCourses(subjectId)
        const courses = res.data || []
        this.subCategories = courses.map(c => ({ id: c.courseId, name: c.courseName }))
        this.activeSubCategory = 0
        if (this.subCategories.length > 0) {
          // 如果当前是考试模式，加载考试信息；否则加载章节
          if (this.activeMode === 2) {
            this.loadExamInfo()
          } else {
            this.loadChapters()
          }
        } else {
          this.chapterTabs = []
          this.chapters = []
          this.modeProgress = { total: 0, done: 0 }
          this.examInfo = null
        }
      } catch (error) {
        console.error('加载科目失败', error)
      }
    },
    // 加载章列表（第三行tab）- dt_chapter表
    async loadChapters() {
      if (this.subCategories.length === 0) return
      const courseId = this.subCategories[this.activeSubCategory].id
      const mode = this.activeMode === 0 ? '1' : '2' // 练习模式=1, 真题模式=2
      try {
        const res = await getChapters(courseId, mode)
        const chapters = res.data || []
        this.chapterTabs = chapters.map(c => ({ id: c.chapterId, name: c.chapterName }))
        this.activeChapter = 0
        // 加载所有章的节
        this.loadAllSections()
        // 加载模式进度
        this.loadModeProgress()
      } catch (error) {
        console.error('加载章失败', error)
      }
    },
    // 加载模式进度
    async loadModeProgress() {
      if (this.subCategories.length === 0) {
        this.modeProgress = { total: 0, done: 0 }
        return
      }
      const mode = this.activeMode === 0 ? '1' : '2'
      const courseId = this.subCategories[this.activeSubCategory].id
      try {
        const res = await getModeProgress(mode, courseId)
        this.modeProgress = res.data || { total: 0, done: 0 }
      } catch (error) {
        console.error('加载进度失败', error)
      }
    },
    // 加载所有章的节列表（卡片区域）- 每个章显示为一个分组
    async loadAllSections() {
      if (this.chapterTabs.length === 0) {
        this.chapters = []
        return
      }
      const mode = this.activeMode === 0 ? '1' : '2' // 练习模式=1, 真题模式=2
      try {
        // 并行加载所有章的节
        const promises = this.chapterTabs.map(ch => getSections(ch.id, mode))
        const results = await Promise.all(promises)
        
        // 组装数据：每个章作为一个分组，下面是节列表
        this.chapters = this.chapterTabs.map((ch, index) => {
          const sections = results[index].data || []
          return {
            title: ch.name,
            topics: sections.map(s => ({
              id: s.sectionId,
              name: s.sectionName,
              done: s.done || 0,
              total: s.total || 0
            }))
          }
        }).filter(ch => ch.topics.length > 0) // 过滤掉没有节的章
      } catch (error) {
        console.error('加载节失败', error)
      }
    },
    selectDay(index) {
      this.activeDay = index
    },
    selectCategory(index) {
      this.activeCategory = index
      this.activeSubCategory = 0
      this.activeChapter = 0
      this.loadCourses()
    },
    selectSubCategory(index) {
      this.activeSubCategory = index
      this.activeChapter = 0
      // 如果当前是考试模式，重新加载考试信息
      if (this.activeMode === 2) {
        this.loadExamInfo()
      } else {
        this.loadChapters()
      }
    },
    // 点击章tab时滚动到对应位置
    selectChapter(index) {
      this.activeChapter = index
      // 滚动到对应章节
      this.$nextTick(() => {
        const sections = document.querySelectorAll('.chapter-section')
        if (sections[index]) {
          sections[index].scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      })
    },
    selectMode(index) {
      if (index === 2) {
        // 考试模式加载考试信息
        this.activeMode = index
        // 如果科目列表还没加载，先等待加载完成
        if (this.subCategories.length === 0) {
          // 科目列表为空，可能还在加载中，延迟执行
          this.$nextTick(() => {
            this.loadExamInfo()
          })
        } else {
          this.loadExamInfo()
        }
        return
      }
      this.activeMode = index
      this.activeChapter = 0
      this.loadChapters()
    },
    async loadExamInfo() {
      // 使用请求ID来防止旧请求覆盖新请求的结果
      const requestId = Date.now()
      this.examRequestId = requestId
      
      try {
        // 如果科目列表为空，等待一下再获取
        if (this.subCategories.length === 0) {
          // 等待100ms后重试，最多重试10次
          let retryCount = 0
          while (this.subCategories.length === 0 && retryCount < 10) {
            await new Promise(resolve => setTimeout(resolve, 100))
            retryCount++
          }
        }
        
        // 如果还是没有科目，不发请求
        if (this.subCategories.length === 0) {
          console.log('loadExamInfo - 没有科目数据，跳过请求')
          return
        }
        
        // 获取当前选中的课程ID
        const courseId = this.subCategories[this.activeSubCategory].id;
        console.log('loadExamInfo - courseId:', courseId);
        
        const res = await getLatestExam(courseId)
        
        // 检查是否是最新的请求
        if (this.examRequestId !== requestId) {
          console.log('loadExamInfo - 请求已过期，忽略结果')
          return
        }
        
        console.log('loadExamInfo - response:', res);
        this.examInfo = res.data
        if (this.examInfo && this.examInfo.startTime) {
          this.startCountdown()
        }
      } catch (error) {
        console.error('加载考试信息失败', error)
      }
    },
    startCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
      }
      this.updateCountdown()
      this.countdownTimer = setInterval(() => {
        this.updateCountdown()
      }, 1000)
    },
    updateCountdown() {
      if (!this.examInfo || !this.examInfo.startTime) return
      
      const now = new Date().getTime()
      const startTime = new Date(this.examInfo.startTime).getTime()
      const endTime = new Date(this.examInfo.endTime).getTime()
      const diff = startTime - now
      
      if (diff <= 0) {
        // 考试已开始，更新状态
        this.countdown = { days: '00', hours: '00', minutes: '00', seconds: '00' }
        if (now < endTime) {
          // 考试进行中
          this.examInfo.status = '1'
        } else {
          // 考试已结束
          this.examInfo.status = '2'
        }
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer)
        }
        return
      }
      
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
      const seconds = Math.floor((diff % (1000 * 60)) / 1000)
      
      this.countdown = {
        days: String(days).padStart(2, '0'),
        hours: String(hours).padStart(2, '0'),
        minutes: String(minutes).padStart(2, '0'),
        seconds: String(seconds).padStart(2, '0')
      }
    },
    goActivityDetail(activityId) {
      this.$router.push('/activity/' + encodeId(activityId))
    },
    goToTopic(topic) {
      if (this.activeMode === 2) return // 考试模式不跳转
      const mode = this.activeMode === 0 ? '1' : '2'
      this.$router.push('/question/' + encodeId(topic.id) + '?mode=' + mode)
    },
    goLogin() {
      this.$router.push('/login')
    },
    goContest() {
      this.$router.push('/contest')
    },
    handleLogout() {
      localStorage.removeItem('userInfo')
      this.userInfo = null
      this.showUserMenu = false
      this.$router.push('/login')
    },
    goProfile() {
      this.showUserMenu = false
      this.$router.push('/profile')
    },
    goFavorite() {
      this.showUserMenu = false
      this.$router.push('/favorite')
    },
    goExamRecord() {
      this.showUserMenu = false
      this.$router.push('/exam-record')
    },
    formatExamTime(time) {
      if (!time) return ''
      const d = new Date(time)
      const year = d.getFullYear()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      const hour = String(d.getHours()).padStart(2, '0')
      const minute = String(d.getMinutes()).padStart(2, '0')
      const second = String(d.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`
    },
    async startExam() {
      if (!this.examInfo || this.examInfo.status !== '1') return
      
      try {
        const res = await startExam(this.examInfo.examId)
        if (res.code === 200) {
          const data = res.data
          if (data.hasSubmitted) {
            // 已经提交过，跳转到答题详情页
            this.$router.push('/exam-record-detail/' + encodeId(data.recordId))
          } else {
            // 未提交，进入考试页面
            this.$router.push('/exam/' + encodeId(this.examInfo.examId))
          }
        } else {
          alert(res.msg || '进入考试失败')
        }
      } catch (error) {
        console.error('进入考试失败', error)
        alert('进入考试失败')
      }
    }
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside)
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer)
    }
  }
}
</script>

<style scoped>
.home-container {
  min-height: 100vh;
  background: #f5f5f5;
  position: relative;
}

.banner {
  height: 0;
  padding-bottom: 20%;
  background-image: url('@/assets/image/back-home.jpeg');
  background-size: 100% auto;
  background-position: top center;
  background-repeat: no-repeat;
}

.banner-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px 40px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.logo-area {
  display: flex;
  align-items: center;
  gap: 10px;
}

.logo {
  color: #3d8c40;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
}

.logo-img {
  width: 40px;
  height: 40px;
  border-radius: 6px;
  object-fit: contain;
}

.system-name {
  color: #3d8c40;
  font-size: 20px;
  font-weight: 600;
}

.user-area {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #333;
  font-size: 14px;
  cursor: pointer;
}

.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.user-email {
  font-size: 14px;
  color: #333;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-menu {
  position: absolute;
  top: 50px;
  right: 0;
  width: 220px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  z-index: 100;
  overflow: hidden;
}

.menu-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 15px;
}

.menu-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  object-fit: cover;
}

.menu-nickname {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.menu-divider {
  height: 1px;
  background: #eee;
  margin: 0 15px;
}

.menu-actions {
  display: flex;
  justify-content: space-around;
  padding: 15px 10px;
}

.menu-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  cursor: pointer;
}

.menu-icon {
  width: 32px;
  height: 32px;
}

.menu-item span {
  font-size: 12px;
  color: #333;
}

.menu-logout {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 12px;
  border-top: 1px solid #eee;
  cursor: pointer;
  color: #666;
  font-size: 13px;
}

.menu-logout:hover {
  background: #f5f5f5;
}

.logout-icon {
  width: 16px;
  height: 16px;
}

.login-btn {
  padding: 8px 24px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
}

.login-btn:hover {
  background: #2e7d32;
}

.main-content {
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px 40px;
  position: relative;
  z-index: 1;
  overflow: visible;
}

.category-tabs {
  display: flex;
  justify-content: center;
  gap: 80px;
  margin-bottom: 20px;
  padding: 15px 0;
}

.tab {
  font-size: 18px;
  color: #666;
  cursor: pointer;
  padding: 10px 0;
  transition: color 0.2s;
  position: relative;
}

.tab:hover {
  color: #3d8c40;
}

.tab.active {
  color: #3d8c40;
  font-weight: 500;
}

.tab.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 30px;
  height: 3px;
  background: #3d8c40;
  border-radius: 2px;
}

.sub-tabs {
  display: flex;
  justify-content: center;
  gap: 30px;
  margin-bottom: 25px;
}

.sub-tab {
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 5px 0;
  transition: color 0.2s;
}

.sub-tab:hover {
  color: #3d8c40;
}

.sub-tab.active {
  color: #3d8c40;
  border-bottom: 2px solid #3d8c40;
}

.chapter-tabs {
  display: flex;
  gap: 20px;
  padding: 15px 25px;
  border-bottom: 1px solid #f0f0f0;
  overflow-x: auto;
}

.chapter-tab {
  font-size: 14px;
  color: #666;
  cursor: pointer;
  padding: 5px 10px;
  transition: all 0.2s;
  white-space: nowrap;
  border-radius: 4px;
}

.chapter-tab:hover {
  color: #3d8c40;
  background: #f0f7f0;
}

.chapter-tab.active {
  color: #fff;
  background: #3d8c40;
}

.content-wrapper {
  display: flex;
  gap: 20px;
  align-items: stretch;
}

.left-content {
  flex: 1;
  background: #fff;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.mode-tabs {
  display: flex;
  background: #d4e8d4;
  padding: 8px 15px;
  gap: 0;
}

.mode-tab {
  flex: 1;
  text-align: center;
  padding: 12px 0;
  font-size: 15px;
  color: #666;
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 6px;
}

.mode-tab:hover {
  color: #3d8c40;
}

.mode-tab.active {
  background: #3d8c40;
  color: #fff;
}

.progress-bar-wrapper {
  padding: 15px 25px;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
  color: #666;
}

.progress-text {
  font-weight: 500;
}

.progress-text.green {
  color: #3d8c40;
}

.progress-text.red {
  color: #e74c3c;
}

.progress-bar {
  height: 8px;
  background: #e8e8e8;
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #3d8c40, #5cb85c);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.chapters-wrapper {
  flex: 1;
  max-height: 300px;
  overflow-y: auto;
}

.chapter-section {
  padding: 20px 25px;
}

.chapter-title {
  font-size: 16px;
  color: #333;
  margin-bottom: 15px;
  font-weight: 500;
}

.topic-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 15px;
}

.topic-card {
  background: #f5f5f5;
  border: 1px solid #e8e8e8;
  border-radius: 6px;
  padding: 15px;
  cursor: pointer;
  transition: all 0.2s;
}

.topic-card:hover {
  border-color: #3d8c40;
  box-shadow: 0 2px 8px rgba(61, 140, 64, 0.15);
}

.topic-name {
  font-size: 15px;
  color: #333;
  margin-bottom: 8px;
}

.topic-progress {
  font-size: 12px;
  color: #999;
}

.topic-progress .done {
  color: #3d8c40;
  margin: 0 2px;
}

.content-decor {
  height: 180px;
  background-image: url('@/assets/image/back-mulu.jpeg');
  background-size: 100% auto;
  background-position: bottom center;
  background-repeat: no-repeat;
  margin-top: auto;
}

/* 考试模式样式 */
.exam-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.exam-content {
  display: flex;
  flex-direction: column;
  padding: 25px;
  gap: 20px;
}

.exam-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 25px;
  background: linear-gradient(135deg, #f0f7f0 0%, #e8f5e9 100%);
  border-radius: 8px;
  gap: 20px;
}

.no-exam {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
  font-size: 14px;
}

.countdown-box {
  flex: 1;
}

.exam-title {
  font-size: 18px;
  color: #333;
  font-weight: 600;
  margin-bottom: 8px;
}

.countdown-title {
  font-size: 16px;
  color: #3d8c40;
  font-weight: 500;
  margin-bottom: 12px;
}

.countdown-numbers {
  display: flex;
  gap: 20px;
  margin-bottom: 12px;
}

.countdown-item {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.countdown-item .num {
  font-size: 32px;
  color: #3d8c40;
  font-weight: 600;
}

.countdown-item .label {
  font-size: 14px;
  color: #666;
}

.exam-time-info {
  font-size: 13px;
  color: #666;
  display: flex;
  gap: 8px;
}

.exam-time-info .divider {
  color: #ddd;
}

.start-exam-btn {
  padding: 12px 40px;
  background: #3d8c40;
  color: #fff;
  border: none;
  border-radius: 25px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.start-exam-btn:hover {
  background: #2e7d32;
  transform: scale(1.02);
}

.start-exam-btn.disabled {
  background: #ccc;
  cursor: not-allowed;
}

.start-exam-btn.disabled:hover {
  background: #ccc;
  transform: none;
}

.exam-notice {
  background: #fafafa;
  border-radius: 8px;
  padding: 20px;
}

.exam-notice h3 {
  font-size: 15px;
  color: #333;
  font-weight: 500;
  margin: 0 0 15px 0;
}

.notice-content {
  font-size: 14px;
  color: #666;
  line-height: 1.8;
}

.notice-content p {
  margin: 0 0 8px 0;
}

.notice-content p:last-child {
  margin-bottom: 0;
}

.right-sidebar {
  width: 420px;
}

.sidebar-card {
  background: #fff;
  border-radius: 8px;
  overflow: visible;
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.week-header {
  display: flex;
  justify-content: space-between;
  padding: 20px 20px;
  border-bottom: 1px solid #f0f0f0;
}

.week-day {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 40px;
}

.week-day:hover {
  background: #f0f7f0;
}

.week-day.active {
  background: #d4e8d4;
}

.week-day.active .day-label,
.week-day.active .day-date {
  color: #3d8c40;
  font-weight: 500;
}

.week-day.has-activity .day-date::after {
  content: '';
  display: block;
  width: 4px;
  height: 4px;
  background: #3d8c40;
  border-radius: 50%;
  margin: 4px auto 0;
}

.day-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 6px;
  white-space: nowrap;
}

.day-date {
  font-size: 16px;
  color: #333;
  font-weight: 500;
}

.activity-section {
  padding: 15px;
  margin: 15px;
  background: #f8f8f8;
  border-radius: 6px;
  min-height: 60px;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0;
  border-bottom: 1px solid #eee;
  cursor: pointer;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-item:hover {
  background: #f0f7f0;
  margin: 0 -10px;
  padding: 10px;
  border-radius: 4px;
}

.activity-type {
  font-size: 13px;
  color: #3d8c40;
  font-weight: 500;
  flex-shrink: 0;
  width: 50px;
  text-align: center;
}

.activity-title {
  font-size: 14px;
  color: #333;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.no-activity {
  text-align: center;
  color: #999;
  font-size: 13px;
  padding: 15px 0;
}

.view-more {
  font-size: 12px;
  color: #999;
  text-decoration: none;
  flex-shrink: 0;
}

.view-more:hover {
  color: #3d8c40;
}

.sidebar-decor {
  flex: 1;
  min-height: 200px;
  background-image: url('@/assets/image/back-date.jpeg');
  background-size: contain;
  background-position: bottom right;
  background-repeat: no-repeat;
  margin-top: auto;
}

.footer {
  text-align: center;
  padding: 30px 0;
  font-size: 12px;
  color: #999;
  line-height: 1.8;
}

/* 移动端适配 */
@media screen and (max-width: 768px) {
  .banner-content {
    padding: 15px 20px;
  }

  .logo {
    font-size: 18px;
  }

  .user-info span {
    display: none;
  }

  .avatar {
    width: 32px;
    height: 32px;
  }

  .user-menu {
    width: 260px;
    right: -10px;
  }

  .main-content {
    padding: 0 15px 30px;
  }

  .category-tabs {
    gap: 30px;
    padding: 12px 0;
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .category-tabs::-webkit-scrollbar {
    display: none;
  }

  .tab {
    font-size: 15px;
    white-space: nowrap;
  }

  .sub-tabs {
    gap: 15px;
    overflow-x: auto;
    padding: 0 10px;
    justify-content: flex-start;
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .sub-tabs::-webkit-scrollbar {
    display: none;
  }

  .sub-tab {
    font-size: 13px;
    white-space: nowrap;
  }

  .chapter-tabs {
    padding: 10px 15px;
    gap: 10px;
  }

  .chapter-tab {
    font-size: 13px;
    padding: 4px 8px;
  }

  .content-wrapper {
    flex-direction: column;
  }

  .right-sidebar {
    width: 100%;
    order: -1;
  }

  .sidebar-card {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .week-header {
    width: 100%;
    padding: 15px 10px;
  }

  .activity-section {
    width: 100%;
    margin: 10px;
  }

  .activity-item {
    flex-wrap: wrap;
  }

  .activity-title {
    width: calc(100% - 80px);
    order: 2;
  }

  .view-more {
    order: 3;
    margin-left: auto;
  }

  .sidebar-decor {
    display: none;
  }

  .mode-tabs {
    padding: 6px 10px;
  }

  .mode-tab {
    padding: 10px 0;
    font-size: 14px;
  }

  .chapter-section {
    padding: 15px;
  }

  .topic-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }

  .topic-card {
    padding: 12px;
  }

  .topic-name {
    font-size: 14px;
  }

  .content-decor {
    height: 120px;
  }

  /* 考试模式移动端适配 */
  .exam-content {
    padding: 15px;
    gap: 15px;
  }

  .exam-header {
    flex-direction: column;
    padding: 15px;
    gap: 15px;
  }

  .countdown-numbers {
    justify-content: center;
  }

  .countdown-item .num {
    font-size: 24px;
  }

  .exam-time-info {
    flex-wrap: wrap;
    justify-content: center;
  }

  .start-exam-btn {
    width: 100%;
    padding: 12px 30px;
  }

  .exam-notice {
    padding: 15px;
  }

  .exam-notice h3 {
    font-size: 14px;
  }

  .notice-content {
    font-size: 13px;
  }

  .footer {
    padding: 20px 15px;
  }
}

@media screen and (max-width: 480px) {
  .category-tabs {
    gap: 20px;
  }

  .tab {
    font-size: 14px;
  }

  .week-day {
    padding: 6px 4px;
    min-width: 30px;
  }

  .day-label {
    font-size: 12px;
  }

  .day-date {
    font-size: 14px;
  }

  .activity-type {
    font-size: 12px;
  }

  .activity-title {
    font-size: 13px;
  }

  .countdown-numbers {
    gap: 10px;
  }

  .countdown-item .num {
    font-size: 20px;
  }
}
</style>
